import { UserServiceClient } from 'fitbeat.api-clients';
import { StrengthAssessmentStep } from 'fitbeat.gym-core';
import React, { PropsWithChildren } from 'react';
import { SafeAreaView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { connect } from 'react-redux';
import { Button, Icon } from '../components';
import { getApiConfig } from '../config/index';
import { IAppStore } from '../HomeWorkouts';
import theme from '../theme';
import { toggleStrengthAssessmentModal } from '../ui/redux/uiActions';
import { AuthTokenBuilder } from '../utils';
import { Logger } from '../utils/Logger';
import { defaultScale } from '../utils/scaling';
import { completeStrengthAssessment } from './actions';
import { ProgressBar } from './ProgressBar';
import {
  selectIsAssessmentResultLoading,
  selectIsFirstStep,
  selectIsLastStep,
  selectNextButtonDisabled,
  selectStrengthAssessmentStepProgress,
} from './selectors';

interface IStateProps {
  isNextDisabled: boolean;
  isAssessmentResultLoading: boolean;
  stepAssessmentProgress: { from: number; to: number } | null;
  isFirstStep: boolean;
  isLastStep: boolean;
}

interface IDispatchProps {
  onComplete(): Promise<void>;
  closeStrengthAssessmentModal(): void;
}
interface IOwnProps {
  description: string;
  currentStep: StrengthAssessmentStep;
  buttonText: string;
  title: string;
  getNextStep(): void;
  getPreviousStep(): void;
}

type IProps = IOwnProps & IStateProps & IDispatchProps;

const StrengthAssessmentContainerComponent: React.FunctionComponent<
  PropsWithChildren<IProps>
> = ({
  children,
  currentStep,
  buttonText,
  title,
  description,
  isNextDisabled,
  getNextStep,
  getPreviousStep,
  isLastStep,
  isFirstStep,
  closeStrengthAssessmentModal,
  isAssessmentResultLoading,
  onComplete,
  stepAssessmentProgress,
}) => {
  const { questionId } = currentStep;
  const iconSize = defaultScale(25);
  const showBackButton = !isFirstStep;
  const onCompleteAssessment = async () => {
    await onComplete();
    closeStrengthAssessmentModal();
  };
  const handleGetNext = () => {
    return isLastStep ? onCompleteAssessment() : getNextStep();
  };
  const showProgressBar = !!stepAssessmentProgress;
  const showContainerButton =
    questionId !== 'ManualSelection' &&
    questionId !== 'GeneralStrengthLevel' &&
    questionId !== 'StrengthSettings';

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.descriptionAndTitleContainer}>
        <View style={styles.headingContainer}>
          {showBackButton && (
            <TouchableOpacity onPress={getPreviousStep} hitSlop={hitSlop}>
              <Icon
                name='ios-arrow-back'
                color={theme.PRIMARY}
                size={defaultScale(iconSize)}
              />
            </TouchableOpacity>
          )}
          <View style={styles.progressContainer}>
            {showProgressBar && (
              <ProgressBar
                fromValue={stepAssessmentProgress.from}
                toValue={stepAssessmentProgress.to}
              />
            )}
          </View>
          <TouchableOpacity
            hitSlop={hitSlop}
            style={[!showBackButton && styles.closeIcon]}
            onPress={closeStrengthAssessmentModal}
          >
            <Icon
              name='md-close'
              color={theme.GREY}
              size={defaultScale(iconSize)}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.titleText}>{title}</Text>
        <Text style={styles.descriptionText}>{description}</Text>
      </View>
      <View style={styles.container}>{children}</View>
      {showContainerButton && (
        <Button
          title={buttonText}
          fontColor={theme.WHITE}
          isLoading={isAssessmentResultLoading}
          onPress={handleGetNext}
          disabled={isNextDisabled}
          containerStyle={styles.buttonContainer}
        />
      )}
    </SafeAreaView>
  );
};
const mapStateToProps = (state: IAppStore, ownProps: IOwnProps) => {
  const questionId = ownProps.currentStep.questionId;
  return {
    isNextDisabled: selectNextButtonDisabled(state, ownProps.currentStep),
    isAssessmentResultLoading: selectIsAssessmentResultLoading(state),
    stepAssessmentProgress: selectStrengthAssessmentStepProgress(
      state,
      ownProps.currentStep,
    ),
    isFirstStep: selectIsFirstStep(state, questionId),
    isLastStep: selectIsLastStep(state, questionId),
  };
};

const mapDispatchToProps = (dispatch) => {
  const userServiceClient = new UserServiceClient(
    getApiConfig(),
    fetch,
    Logger.debug,
    new AuthTokenBuilder(),
  );
  return {
    closeStrengthAssessmentModal: () =>
      dispatch(toggleStrengthAssessmentModal(false)),
    onComplete: async () =>
      dispatch(completeStrengthAssessment(userServiceClient)),
  };
};

const hitSlop = {
  bottom: defaultScale(20),
  top: defaultScale(20),
  left: defaultScale(20),
  right: defaultScale(20),
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.WHITE,
  },
  descriptionText: {
    ...theme.textStyle.bodyCopyReg,
  },
  titleText: {
    ...theme.textStyle.heading,
    fontWeight: '700',
    marginVertical: defaultScale(5),
  },
  buttonContainer: {
    marginBottom: defaultScale(15),
    marginHorizontal: defaultScale(15),
    height: defaultScale(45),
  },
  descriptionAndTitleContainer: {
    marginHorizontal: defaultScale(15),
  },
  headingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: defaultScale(10),
  },
  closeIcon: {
    alignItems: 'flex-end',
  },
  progressContainer: {
    flex: 1,
    paddingHorizontal: defaultScale(15),
  },
});

export const StrengthAssessmentContainer: React.FC<IOwnProps> = connect<
  IStateProps,
  IDispatchProps,
  IOwnProps,
  IAppStore
>(
  mapStateToProps,
  mapDispatchToProps,
)(StrengthAssessmentContainerComponent);
